import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import PWAInstallButton from './PWAInstallButton';
import GuidedTour from './GuidedTour';
import WhatsNew, { openWhatsNew } from './WhatsNew';
import TrialBanner from './TrialBanner';
import UsageAlertBanner from './UsageAlertBanner';
import DemoBanner from './DemoBanner';
import ScenarioTour from './ScenarioTour';
import ThemeToggleButton from './ThemeToggleButton';
import WarehouseSwitcher from './WarehouseSwitcher';

import {
  House,
  Package,
  MapPin,
  ShoppingCart,
  HandGrabbing,
  BoxArrowDown,
  Truck,
  FileArrowDown,
  ChartBar,
  Users,
  Gear,
  SignOut,
  List,
  X,
  Barcode,
  Factory,
  Buildings,
  UserPlus,
  Warning,
  CaretDown,
  CaretLeft,
  CaretRight,
  ArrowsLeftRight,
  ChartLineUp,
  ChartPieSlice,
  Trophy,
  Shield,
  Archive,
  ChartBar as ChartBarIcon,
  Eye as EyeIcon,
  FileZip,
  ChartLine,
  UploadSimple,
  LockKey,
  Sparkle,
  Megaphone,
  ArrowUUpLeft,
  ShieldWarning,
  ClipboardText,
  ShippingContainer,
  PushPin,
  PushPinSlash,
  DotsSixVertical,
  Clock,
  Calculator,
  PaintBrush,
  Target,
  Stack,
} from '@phosphor-icons/react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { BookOpen } from '@phosphor-icons/react';
import { UserCircle } from '@phosphor-icons/react';
import { Tag } from '@phosphor-icons/react';
import { Plug } from '@phosphor-icons/react';
import { SquaresFour } from '@phosphor-icons/react';
import { FileText } from '@phosphor-icons/react';
import { Heartbeat } from '@phosphor-icons/react';
import { Lightning } from '@phosphor-icons/react';
import { Receipt } from '@phosphor-icons/react';
import { User } from '@phosphor-icons/react';
import { Coins } from '@phosphor-icons/react';
import { Ticket } from '@phosphor-icons/react';
import { Crown } from '@phosphor-icons/react';
import { Key } from '@phosphor-icons/react';

const navGroups = [
  {
    key: 'overview',
    labelKey: 'nav_group.overview',
    label: 'Overview',
    icon: ChartPieSlice,
    items: [
      { path: '/', labelKey: 'nav.dashboard', label: 'Dashboard', icon: House, roles: ['admin', 'manager', 'viewer'] },
      { path: '/analytics', labelKey: 'nav.analytics', label: 'Analytics', icon: ChartBarIcon, roles: ['admin', 'manager', 'viewer'] },
      { path: '/margins', labelKey: 'nav.margins', label: 'Margins', icon: ChartLineUp, roles: ['admin', 'manager'] },
      { path: '/reports', labelKey: 'nav.reports', label: 'Reports', icon: ChartBar, roles: ['admin', 'manager', 'viewer'] },
      { path: '/reports/on-hand', labelKey: 'nav.reports_on_hand', label: 'Stock On-Hand', icon: ChartBar, roles: ['admin', 'manager', 'viewer'] },
      { path: '/reports/stock-valuation', labelKey: 'nav.reports_valuation', label: 'Stock Valuation', icon: Coins, roles: ['admin', 'manager', 'viewer'] },
      { path: '/custom-reports', labelKey: 'nav.custom_reports', label: 'Custom Reports', icon: ChartLine, roles: ['admin', 'manager'] },
      { path: '/warehouse-kpis', labelKey: 'nav.warehouse_kpis', label: 'WH KPIs', icon: ChartPieSlice, roles: ['admin', 'manager'] },
      { path: '/leaderboard', labelKey: 'nav.leaderboard', label: 'Leaderboard', icon: Trophy, roles: ['admin', 'manager', 'picker', 'packer', 'viewer'] },
    ],
  },
  {
    key: 'inventory',
    labelKey: 'nav_group.inventory',
    label: 'Inventory Management',
    icon: Package,
    items: [
      { path: '/inventory', labelKey: 'nav.inventory', label: 'Inventory', icon: Package, roles: ['admin', 'manager', 'viewer'] },
      { path: '/inventory/bulk-load', labelKey: 'nav.bulk_load', label: 'Bulk-Load Stock (CSV)', icon: Package, roles: ['admin'] },
      { path: '/inventory/sku-volume-audit', labelKey: 'nav.sku_volume_audit', label: 'SKU Volume Audit', icon: Package, roles: ['admin', 'manager'] },
      { path: '/serials', labelKey: 'nav.serials', label: 'Serials / IMEI', icon: Barcode, roles: ['admin', 'manager', 'viewer', 'picker', 'packer'] },
      { path: '/lookup', labelKey: 'nav.lookup', label: 'Stock Lookup', icon: MagnifyingGlass, roles: ['admin', 'manager', 'viewer', 'picker', 'packer'] },
      { path: '/locations', labelKey: 'nav.locations', label: 'Locations', icon: MapPin, roles: ['admin', 'manager', 'viewer'] },
      { path: '/settings/location-schema', labelKey: 'nav.location_schema', label: 'Location Code Schema', icon: MapPin, roles: ['admin', 'manager'] },
      { path: '/stock-moves', labelKey: 'nav.stock_moves', label: 'Stock Moves', icon: ArrowsLeftRight, roles: ['admin', 'manager', 'picker'] },
      { path: '/fixed-locations', labelKey: 'nav.fixed_locations', label: 'Fixed Locations', icon: MapPin, roles: ['admin', 'manager', 'viewer'] },
      { path: '/warehouses', labelKey: 'nav.warehouses', label: 'Warehouses', icon: Buildings, roles: ['admin'] },
      { path: '/warehouse-layout', labelKey: 'nav.warehouse_layout', label: 'Layout Designer', icon: SquaresFour, roles: ['admin', 'manager'] },
      { path: '/cycle-counts', labelKey: 'nav.cycle_counts', label: 'Cycle Counts', icon: ClipboardText, roles: ['admin', 'manager', 'viewer', 'picker'] },
      { path: '/discrepancies', labelKey: 'nav.discrepancies', label: 'Discrepancy Investigation', icon: MagnifyingGlass, roles: ['admin', 'manager', 'viewer'] },
      { path: '/location-reports', labelKey: 'nav.location_reports', label: 'Location Reports', icon: MapPin, roles: ['admin', 'manager', 'viewer'] },
    ],
  },
  {
    key: 'inbound',
    labelKey: 'nav_group.inbound',
    label: 'Inbound',
    icon: FileArrowDown,
    items: [
      { path: '/receiving', labelKey: 'nav.receiving', label: 'Receiving', icon: FileArrowDown, roles: ['admin', 'manager'] },
      { path: '/receipt-templates', labelKey: 'nav.receipt_templates', label: 'Supplier Templates', icon: FileText, roles: ['admin', 'manager'] },
      { path: '/reorder-suggestions', labelKey: 'nav.reorder_suggestions', label: 'Reorder Suggestions', icon: Lightning, roles: ['admin', 'manager'] },
      { path: '/containers', labelKey: 'nav.containers', label: 'Containers', icon: ShippingContainer, roles: ['admin', 'manager', 'viewer', 'picker', 'packer'] },
      { path: '/quarantine', labelKey: 'nav.quarantine', label: 'QA Hold', icon: Shield, roles: ['admin', 'manager'] },
      { path: '/quarantine-review', labelKey: 'nav.quarantine_review', label: 'Quarantine Review', icon: ShieldWarning, roles: ['admin', 'manager'] },
    ],
  },
  {
    key: 'outbound',
    labelKey: 'nav_group.outbound',
    label: 'Outbound',
    icon: Truck,
    items: [
      { path: '/orders', labelKey: 'nav.orders', label: 'Orders', icon: ShoppingCart, roles: ['admin', 'manager', 'viewer'] },
      { path: '/backorders', labelKey: 'nav.backorders', label: 'Backorders', icon: Warning, roles: ['admin', 'manager', 'viewer'] },
      { path: '/picking', labelKey: 'nav.picking', label: 'Picking', icon: HandGrabbing, roles: ['admin', 'manager', 'picker'] },
      { path: '/operator', labelKey: 'nav.operator', label: 'Operator Console', icon: Lightning, roles: ['admin', 'manager', 'picker', 'packer'] },
      { path: '/packing', labelKey: 'nav.packing', label: 'Packing', icon: BoxArrowDown, roles: ['admin', 'manager', 'packer'] },
      { path: '/dispatch', labelKey: 'nav.dispatch', label: 'Dispatch', icon: ShippingContainer, roles: ['admin', 'manager', 'packer', 'viewer'] },
      { path: '/drivers', labelKey: 'nav.drivers', label: 'Drivers', icon: Truck, roles: ['admin', 'manager'] },
      { path: '/shipping', labelKey: 'nav.shipping', label: 'Shipping', icon: Truck, roles: ['admin', 'manager', 'packer'] },
      { path: '/invoices/outstanding', labelKey: 'nav.outstanding_invoices', label: 'Outstanding Invoices', icon: Receipt, roles: ['admin', 'manager', 'viewer'] },
      { path: '/invoices/customers', labelKey: 'nav.customers', label: 'Customers', icon: User, roles: ['admin', 'manager', 'viewer'] },
    ],
  },
  {
    key: 'transport',
    labelKey: 'nav_group.transport',
    label: 'Transport',
    icon: Truck,
    items: [
      { path: '/transport', labelKey: 'nav.transport', label: 'Transport (Board + Jobs)', icon: Truck, roles: ['admin', 'manager', 'viewer'] },
    ],
  },
  {
    key: 'finance',
    labelKey: 'nav_group.finance',
    label: 'Finance & Pricing',
    icon: Coins,
    items: [
      { path: '/settings/pricing', labelKey: 'nav.pricing_rules', label: 'Commercial Pricing', icon: Coins, roles: ['admin', 'manager'] },
      { path: '/settings/customer-pricing', labelKey: 'nav.customer_pricing', label: 'Customer Price List', icon: Tag, roles: ['admin', 'manager'] },
      { path: '/reports/margin-floor-audit', labelKey: 'nav.margin_floor_audit', label: 'Margin Floor Audit', icon: Shield, roles: ['admin', 'manager'] },
      { path: '/settings/promo-codes', labelKey: 'nav.promo_codes', label: 'SaaS Discount Codes', icon: Ticket, roles: ['admin', 'manager'], requireSuperadmin: true },
      { path: '/settings/order-discounts', labelKey: 'nav.order_discounts', label: 'Order Discounts', icon: Ticket, roles: ['admin', 'manager'] },
      { path: '/3pl-billing', labelKey: 'nav.three_pl_billing', label: '3PL Billing', icon: FileText, roles: ['admin', 'manager'] },
      { path: '/settings/invoices', labelKey: 'nav.invoice_settings', label: 'Invoice Settings', icon: Receipt, roles: ['admin', 'manager'] },
      { path: '/margins', labelKey: 'nav.margins', label: 'Margins', icon: ChartLineUp, roles: ['admin', 'manager'] },
    ],
  },
  {
    key: 'returns',
    labelKey: 'nav_group.returns',
    label: 'Returns & Reverse',
    icon: ArrowUUpLeft,
    items: [
      { path: '/returns', labelKey: 'nav.returns', label: 'Returns', icon: ArrowUUpLeft, roles: ['admin', 'manager', 'viewer'] },
      { path: '/retention', labelKey: 'nav.retention', label: 'Retention', icon: Archive, roles: ['admin'] },
    ],
  },
  {
    key: 'internal',
    labelKey: 'nav_group.internal',
    label: 'Internal Movements',
    icon: ArrowsLeftRight,
    items: [
      { path: '/transfers', labelKey: 'nav.transfers', label: 'Transfers', icon: ArrowsLeftRight, roles: ['admin', 'manager', 'picker'] },
      { path: '/transfers/ledger', labelKey: 'nav.transfer_ledger', label: 'Transfer Ledger', icon: ArrowsLeftRight, roles: ['admin', 'manager', 'viewer'] },
      { path: '/transfers/freight-matrix', labelKey: 'nav.freight_matrix', label: 'Freight Rate Matrix', icon: ArrowsLeftRight, roles: ['admin', 'manager'] },
      { path: '/replenishments', labelKey: 'nav.replenishments', label: 'Replenishments', icon: ArrowsLeftRight, roles: ['admin', 'manager', 'picker'] },
      { path: '/pallets', labelKey: 'nav.pallets', label: 'Pallets', icon: Stack, roles: ['admin', 'manager', 'viewer', 'picker', 'packer'] },
      { path: '/pallets/scan', labelKey: 'nav.pallet_scan', label: 'Scan pallets', icon: Barcode, roles: ['admin', 'manager', 'picker', 'packer'] },
    ],
  },
  {
    key: 'production',
    labelKey: 'nav_group.production',
    label: 'Production',
    icon: Factory,
    items: [
      { path: '/manufacturing', labelKey: 'nav.manufacturing', label: 'Manufacturing', icon: Factory, roles: ['admin', 'manager'] },
      { path: '/forecast', labelKey: 'nav.forecast', label: 'Forecast', icon: ChartLineUp, roles: ['admin', 'manager', 'viewer'] },
      { path: '/master-planning', labelKey: 'nav.master_planning', label: 'Master Planning', icon: Calculator, roles: ['admin', 'manager'] },
      { path: '/planning-settings', labelKey: 'nav.planning_settings', label: 'Planning Settings', icon: Calculator, roles: ['admin', 'manager'] },
    ],
  },
  {
    key: 'quality',
    labelKey: 'nav_group.quality',
    label: 'Quality & Compliance',
    icon: Shield,
    items: [
      { path: '/compliance', labelKey: 'nav.compliance', label: 'Compliance', icon: FileZip, roles: ['admin'] },
      { path: '/audit-log', labelKey: 'nav.audit_log', label: 'Audit Log', icon: EyeIcon, roles: ['admin'] },
      { path: '/admin/scheduler', labelKey: 'nav.scheduler', label: 'Scheduler', icon: Clock, roles: ['admin'], requireSuperadmin: true },
      { path: '/admin/scan-settings', labelKey: 'nav.scan_settings', label: 'Scan defaults', icon: Barcode, roles: ['admin', 'manager'] },
    ],
  },
  {
    key: 'monitoring',
    labelKey: 'nav_group.monitoring',
    label: 'Monitoring & Alerts',
    icon: Warning,
    items: [
      { path: '/alerts', labelKey: 'nav.alerts', label: 'Alerts', icon: Warning, roles: ['admin', 'manager'] },
      { path: '/broadcast', labelKey: 'nav.broadcast', label: 'Broadcast', icon: Megaphone, roles: ['admin'] },
    ],
  },
  {
    key: 'system',
    labelKey: 'nav_group.system',
    label: 'User & System',
    icon: Users,
    items: [
      { path: '/users', labelKey: 'nav.users', label: 'Users', icon: Users, roles: ['admin'] },
      { path: '/admin/grant-addon', labelKey: 'nav.grant_addon', label: 'Grant Add-on', icon: Crown, roles: ['admin'], requireSuperadmin: true },
      { path: '/admin/create-business', labelKey: 'nav.create_business', label: 'Create Business', icon: Buildings, roles: ['admin'], requireSuperadmin: true },
      { path: '/admin/recent-signups', labelKey: 'nav.recent_signups', label: 'Recent Signups', icon: UserPlus, roles: ['admin'], requireSuperadmin: true },
      { path: '/admin/competition-analytics', label: 'Competition Analytics', icon: Trophy, roles: ['admin'], requireSuperadmin: true },
      { path: '/settings/account', labelKey: 'nav.account', label: 'My Account', icon: Key, roles: ['admin', 'manager', 'picker', 'packer', 'viewer'] },
      { path: '/settings/company', label: 'Company Settings', icon: Buildings, roles: ['admin', 'manager'] },
      { path: '/security', labelKey: 'nav.security', label: 'Security (2FA)', icon: LockKey, roles: ['admin', 'manager', 'picker', 'packer', 'viewer'] },
      { path: '/settings/appearance', labelKey: 'nav.appearance', label: 'Appearance', icon: PaintBrush, roles: ['admin', 'manager', 'picker', 'packer', 'viewer'] },
      { path: '/3pl-billing', labelKey: 'nav.three_pl_billing', label: '3PL Billing', icon: FileText, roles: ['admin', 'manager'] },
    ],
  },
  {
    key: 'data',
    labelKey: 'nav_group.data',
    label: 'Data & Integration',
    icon: UploadSimple,
    items: [
      { path: '/csv-import', labelKey: 'nav.csv_import', label: 'CSV Import', icon: UploadSimple, roles: ['admin', 'manager'] },
      { path: '/settings/integrations', labelKey: 'nav.integrations', label: 'Integrations', icon: Plug, roles: ['admin', 'manager'] },
      { path: '/data-health', labelKey: 'nav.data_health', label: 'Data Health', icon: Heartbeat, roles: ['admin'] },
      { path: '/release-notes', labelKey: 'nav.release_notes', label: 'Release Notes', icon: Sparkle, roles: ['admin'] },
    ],
  },
  {
    key: 'help',
    labelKey: 'nav_group.help',
    label: 'Help & Training',
    icon: BookOpen,
    items: [
      { path: '/help', labelKey: 'nav.help', label: 'User Manual', icon: BookOpen, roles: ['admin', 'manager', 'picker', 'packer', 'viewer'] },
    ],
  },
];


// Nav paths visible when the tenant is on `product_mode='pallets_only'`.
// Any path NOT in this list gets stripped from the sidebar for pallet-only
// tenants, giving them a focused CHEP/Loscam tracker without the full WMS
// clutter. Existing WMS tenants (`product_mode='wms'`) see everything.
const PALLETS_ONLY_PATHS = new Set([
  '/',                          // dashboard (pallet KPIs surface here)
  '/analytics',                 // analytics with pallet widgets
  '/reports',                   // reports hub
  '/pallets', '/pallets/scan',
  '/warehouses',                // scope only
  '/merchants',                 // required for movement attribution
  '/3pl/billing',               // 3pl merchants tab lives here
  '/integrations', '/settings/integrations',
  '/audit-log',
  '/billing', '/pricing',
  '/settings', '/settings/profile', '/settings/2fa', '/settings/team',
  '/settings/users', '/settings/tenants', '/settings/notifications',
  '/settings/integrations', '/settings/domains',
  '/help', '/docs',
]);

// Nav items hidden when the tenant is a Competition Demo (protects judges
// from touching things that don't belong in an isolated tenant).
const HIDDEN_IN_DEMO = new Set([
  '/users', '/admin/grant-addon', '/admin/create-business',
  '/admin/recent-signups', '/admin/scheduler',
  '/settings/integrations', '/settings/promo-codes',
  '/settings/domains', '/3pl-billing',
  '/data-health', '/release-notes', '/security',
  '/settings/invoices', '/settings/pricing', '/settings/customer-pricing',
  '/retention', '/billing',
]);


export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Filter each group by role, then by product-mode, then drop groups with no visible items.
  const isSuperadmin = user?.platform_role === 'superadmin';
  const productMode = user?.company?.product_mode || 'wms';
  const isDemo = !!user?.company?.is_demo;
  const visibleGroups = navGroups
    .map((g) => ({
      ...g,
      label: g.labelKey ? t(g.labelKey, g.label) : g.label,
      items: g.items
        .filter((it) => it.roles.includes(user?.role))
        .filter((it) => !it.requireSuperadmin || isSuperadmin)
        .filter((it) => productMode !== 'pallets_only' || PALLETS_ONLY_PATHS.has(it.path))
        .filter((it) => !isDemo || !HIDDEN_IN_DEMO.has(it.path))
        .map((it) => ({ ...it, label: it.labelKey ? t(it.labelKey, it.label) : it.label })),
    }))
    .filter((g) => g.items.length > 0);

  // Route guard for pallets-only tenants: if they land on a WMS-only route
  // (e.g. via a bookmarked link or manual URL entry), bounce them to /pallets.
  React.useEffect(() => {
    if (productMode !== 'pallets_only') return;
    const path = location.pathname;
    // Allow the pallets-only set + any sub-path (e.g. /pallets/scan/xyz,
    // /settings/2fa, /billing/invoices) rooted at an allowed prefix.
    const allowed = Array.from(PALLETS_ONLY_PATHS).some((p) => (
      path === p || path.startsWith(p === '/' ? '/__none__' : `${p}/`)
    ));
    if (!allowed) navigate('/pallets', { replace: true });
  }, [location.pathname, productMode, navigate]);

  // Which group contains the currently active route — used to auto-expand.
  const activeGroupKey = React.useMemo(() => {
    const found = visibleGroups.find((g) =>
      g.items.some((it) => location.pathname === it.path)
    );
    return found?.key || visibleGroups[0]?.key || null;
  }, [location.pathname, visibleGroups]);

  // Persist open/closed state per group across navigations.
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('stockaris_nav_open') || '{}');
      if (saved && typeof saved === 'object' && Object.keys(saved).length) return saved;
    } catch { /* ignore */ }
    // Default: only the group containing the current route is open.
    const init = {};
    visibleGroups.forEach((g) => { init[g.key] = false; });
    return init;
  });

  // Ensure the active group auto-opens on navigation.
  React.useEffect(() => {
    if (activeGroupKey) {
      setOpenGroups((prev) => (prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupKey]);

  React.useEffect(() => {
    localStorage.setItem('stockaris_nav_open', JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // ---- Sidebar collapse (desktop only) ----
  // Persist preference per browser. When collapsed, the sidebar slides off-screen
  // and the main content reclaims the full viewport width. A small floating
  // expand handle reappears at the top-left edge so navigation stays one click away.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('stockaris.sidebar.collapsed') === '1'; }
    catch { return false; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('stockaris.sidebar.collapsed', sidebarCollapsed ? '1' : '0'); }
    catch { /* ignore */ }
  }, [sidebarCollapsed]);
  // Cmd/Ctrl + B toggles the sidebar — same shortcut as VS Code / GitHub
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'B')) {
        // Don't fire when user is typing in an input
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
        e.preventDefault();
        setSidebarCollapsed((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---- Focus Mode ----
  // Floor-worker pages (Picking, Packing, Receiving, Count Mode, Driver/Carrier portals)
  // automatically hide ALL chrome — sidebar slides off, bottom nav hides — so the
  // operator gets every pixel for the work-in-progress. A small floating pill
  // appears in the top-right with "Exit focus" so they can opt out per-session.
  // Once they exit, focus stays off until they navigate to a different focus route.
  const FOCUS_ROUTES = React.useMemo(() => [
    '/picking', '/packing', '/receiving', '/count-mode',
    '/driver', '/carrier', '/pallets/scan',
  ], []);
  const isFocusRoute = React.useMemo(() => {
    return FOCUS_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(`${r}/`));
  }, [location.pathname, FOCUS_ROUTES]);

  // Per-route override: when user clicks "Exit focus", we remember which route they exited
  // so we don't re-engage focus mode on that route until they navigate away & back.
  const [focusExitedRoute, setFocusExitedRoute] = useState(null);
  const inFocusMode = isFocusRoute && focusExitedRoute !== location.pathname;

  // Reset the exit override when navigating away from the exited route
  React.useEffect(() => {
    if (focusExitedRoute && location.pathname !== focusExitedRoute) {
      setFocusExitedRoute(null);
    }
  }, [location.pathname, focusExitedRoute]);

  // Effective sidebar collapse: forced collapsed in focus mode, otherwise user preference
  const sidebarHidden = sidebarCollapsed || inFocusMode;

  // ---- Pinned items (per user, persisted to localStorage) ----
  const MAX_PINS = 6;
  const pinStorageKey = user?.id ? `stockaris_pinned_${user.id}` : null;
  const [pinnedPaths, setPinnedPaths] = useState(() => {
    if (!pinStorageKey) return [];
    try {
      const raw = localStorage.getItem(pinStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [contextMenu, setContextMenu] = useState(null); // { path, label, x, y }

  // Reload pins if the user switches (login/logout)
  React.useEffect(() => {
    if (!pinStorageKey) { setPinnedPaths([]); return; }
    try {
      const raw = localStorage.getItem(pinStorageKey);
      setPinnedPaths(raw ? JSON.parse(raw) : []);
    } catch { setPinnedPaths([]); }
  }, [pinStorageKey]);

  const persistPins = (next) => {
    setPinnedPaths(next);
    if (pinStorageKey) localStorage.setItem(pinStorageKey, JSON.stringify(next));
  };

  const togglePin = (path) => {
    if (pinnedPaths.includes(path)) {
      persistPins(pinnedPaths.filter((p) => p !== path));
      toast.success('Unpinned');
    } else {
      if (pinnedPaths.length >= MAX_PINS) {
        toast.error(`Max ${MAX_PINS} pinned items — unpin one first`);
        return;
      }
      persistPins([...pinnedPaths, path]);
      toast.success('Pinned to sidebar');
    }
  };

  // ---- Drag-and-drop reordering of pinned items ----
  // Track drag state in a ref to avoid React's async state updates causing
  // stale reads during the rapid-fire drag event sequence.
  const dragPathRef = React.useRef(null);
  const [dragPath, setDragPath] = useState(null);
  const [dragOverPath, setDragOverPath] = useState(null);

  const handleDragStart = (e, path) => {
    dragPathRef.current = path;
    setDragPath(path);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', path); } catch { /* ignore */ }
  };
  const handleDragOver = (e, path) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (path !== dragOverPath) setDragOverPath(path);
  };
  const handleDrop = (e, targetPath) => {
    e.preventDefault();
    // Prefer ref (set synchronously) then fall back to DataTransfer payload
    // (some browsers only expose it after drop).
    let source = dragPathRef.current;
    if (!source) {
      try { source = e.dataTransfer.getData('text/plain') || null; } catch { /* ignore */ }
    }
    if (!source || source === targetPath) {
      dragPathRef.current = null; setDragPath(null); setDragOverPath(null);
      return;
    }
    const next = pinnedPaths.filter((p) => p !== source);
    const insertAt = next.indexOf(targetPath);
    next.splice(insertAt === -1 ? next.length : insertAt, 0, source);
    persistPins(next);
    dragPathRef.current = null; setDragPath(null); setDragOverPath(null);
  };
  const handleDragEnd = () => {
    dragPathRef.current = null; setDragPath(null); setDragOverPath(null);
  };

  // Keyboard reordering: Alt+ArrowUp / Alt+ArrowDown on a focused pinned row.
  const movePin = (path, direction) => {
    const idx = pinnedPaths.indexOf(path);
    if (idx === -1) return;
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= pinnedPaths.length) return;
    const next = [...pinnedPaths];
    [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
    persistPins(next);
    // Re-focus the moved item so repeated Alt+Arrow keeps working.
    requestAnimationFrame(() => {
      const testId = `nav-pinned-${path.replace(/\W+/g, '') || 'dashboard'}`;
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (el && typeof el.focus === 'function') el.focus();
    });
  };
  const handlePinKeyDown = (e, path) => {
    if (!e.altKey) return;
    if (e.key === 'ArrowUp') { e.preventDefault(); movePin(path, -1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); movePin(path, 1); }
  };

  const openContextMenu = (e, path, label) => {
    e.preventDefault();
    setContextMenu({ path, label, x: e.clientX, y: e.clientY });
  };

  // Dismiss context menu on any click / Escape
  React.useEffect(() => {
    if (!contextMenu) return;
    const close = (ev) => {
      if (ev?.key && ev.key !== 'Escape') return;
      setContextMenu(null);
    };
    const onClick = () => setContextMenu(null);
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', close);
    };
  }, [contextMenu]);

  // Flatten items for pinned-section lookup (respect current role + superadmin gate + product mode)
  const flatItems = React.useMemo(
    () => navGroups.flatMap((g) => g.items
      .filter((it) => it.roles.includes(user?.role))
      .filter((it) => !it.requireSuperadmin || user?.platform_role === 'superadmin')
      .filter((it) => productMode !== 'pallets_only' || PALLETS_ONLY_PATHS.has(it.path))
      .filter((it) => !isDemo || !HIDDEN_IN_DEMO.has(it.path))),
    [user?.role, user?.platform_role, productMode, isDemo],
  );
  const pinnedItems = pinnedPaths
    .map((p) => flatItems.find((it) => it.path === p))
    .filter(Boolean)
    .map((it) => ({ ...it, label: it.labelKey ? t(it.labelKey, it.label) : it.label }));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const renderGroupNav = (isMobile) => (
    <>
      {pinnedItems.length > 0 && (
        <div className="mb-2 pb-2 border-b border-border" data-testid="nav-pinned-section">
          <div className="flex items-center justify-between px-4 py-2">
            <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              <PushPin size={12} weight="fill" className="text-brand" />
              Pinned
            </span>
            <span
              className="text-[10px] font-mono text-muted-foreground"
              title="Drag to reorder · Alt+↑/↓ on a focused pin to move it"
            >
              {pinnedItems.length}/{MAX_PINS}
            </span>
          </div>
          {pinnedItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isDragging = dragPath === item.path;
            const isDragOver = dragOverPath === item.path && dragPath && dragPath !== item.path;
            return (
              <Link
                key={'pinned-' + item.path}
                to={item.path}
                draggable={!isMobile}
                onDragStart={isMobile ? undefined : (e) => handleDragStart(e, item.path)}
                onDragOver={isMobile ? undefined : (e) => handleDragOver(e, item.path)}
                onDrop={isMobile ? undefined : (e) => handleDrop(e, item.path)}
                onDragEnd={isMobile ? undefined : handleDragEnd}
                onKeyDown={isMobile ? undefined : (e) => handlePinKeyDown(e, item.path)}
                aria-label={`${item.label} — pinned shortcut. Press Alt+Up or Alt+Down to reorder.`}
                data-testid={`nav-pinned-${item.path.replace(/\W+/g, '') || 'dashboard'}`}
                onClick={isMobile ? () => setMobileMenuOpen(false) : undefined}
                onContextMenu={(e) => openContextMenu(e, item.path, item.label)}
                className={
                  (isMobile
                    ? `flex items-center gap-3 pl-4 pr-4 py-3 rounded-md ${isActive ? 'bg-accent text-foreground font-semibold' : 'text-muted-foreground'}`
                    : `sidebar-link ${isActive ? 'active' : ''}`)
                  + (isDragging ? ' opacity-40' : '')
                  + (isDragOver ? ' ring-2 ring-inset ring-brand' : '')
                }
              >
                {!isMobile && (
                  <DotsSixVertical
                    size={12}
                    weight="bold"
                    className="cursor-grab text-muted-foreground/50 hover:text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
                <Icon weight={isActive ? 'fill' : 'regular'} size={18} />
                <span className="flex-1">{item.label}</span>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item.path); }}
                  data-testid={`unpin-${item.path.replace(/\W+/g, '') || 'dashboard'}`}
                  title="Unpin"
                  className="p-1 opacity-60 hover:opacity-100 text-muted-foreground hover:text-destructive"
                >
                  <PushPinSlash size={12} weight="bold" />
                </button>
              </Link>
            );
          })}
        </div>
      )}
      {visibleGroups.map((group) => {
        const GroupIcon = group.icon;
        const isOpen = !!openGroups[group.key];
        const hasActiveChild = group.items.some((it) => location.pathname === it.path);
        return (
          <div key={group.key} className="mb-1">
            <button
              type="button"
              onClick={() => toggleGroup(group.key)}
              data-testid={`nav-group-${group.key}`}
              aria-expanded={isOpen}
              className={`w-full flex items-center justify-between px-4 py-2 text-left transition-colors ${
                hasActiveChild ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex items-center gap-3">
                <GroupIcon size={16} weight={hasActiveChild ? 'fill' : 'regular'} />
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em]">{group.label}</span>
              </span>
              <CaretDown
                size={12}
                weight="bold"
                className={`transition-transform ${isOpen ? '' : '-rotate-90'}`}
              />
            </button>
            {isOpen && (
              <div className="pb-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const isPinned = pinnedPaths.includes(item.path);
                  const tourKey = item.path === '/' ? 'dashboard' : item.path.replace('/', '');
                  return (
                    <Link
                      key={group.key + '-' + item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      data-tour={tourKey}
                      onClick={isMobile ? () => setMobileMenuOpen(false) : undefined}
                      onContextMenu={(e) => openContextMenu(e, item.path, item.label)}
                      className={isMobile
                        ? `flex items-center gap-3 pl-10 pr-4 py-3 rounded-md ${isActive ? 'bg-accent text-foreground font-semibold' : 'text-muted-foreground'}`
                        : `sidebar-link pl-10 group relative ${isActive ? 'active' : ''}`}
                    >
                      <Icon weight={isActive ? 'fill' : 'regular'} size={18} />
                      <span className="flex-1">{item.label}</span>
                      {isPinned && (
                        <PushPin
                          size={11}
                          weight="fill"
                          className="text-brand/60"
                        />
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop Sidebar — slides off-screen when collapsed.
          We translate (instead of resize w-0) so the smooth-scroll animation reads natural. */}
      <aside
        className={`hidden md:flex flex-col w-60 app-sidebar fixed h-full z-40 transition-transform duration-200 ease-out ${
          sidebarHidden ? '-translate-x-full' : 'translate-x-0'
        }`}
        data-testid="desktop-sidebar"
        aria-hidden={sidebarHidden}
      >
        <div className="p-4 border-b border-border shrink-0 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-md bg-brand flex items-center justify-center shrink-0">
              <Barcode weight="bold" className="text-brand-foreground" size={20} />
            </div>
            <span className="font-bold text-lg tracking-tight truncate">STOCKARIS</span>
          </Link>
          <button
            type="button"
            onClick={() => setSidebarCollapsed(true)}
            data-testid="sidebar-collapse-btn"
            title="Collapse sidebar (⌘B)"
            aria-label="Collapse sidebar"
            className="p-1.5 -mr-1 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
        </div>
        <nav className="flex-1 py-2 overflow-y-auto sidebar-nav-scroll min-h-0 px-2">
          {renderGroupNav(false)}
        </nav>
        <div className="p-3 border-t border-border space-y-1 shrink-0">
          <div className="flex items-center gap-3 mb-2 px-1">
            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center font-semibold text-foreground text-sm">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate text-foreground">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-1">
            <LanguageSwitcher compact dropUp />
            <PWAInstallButton compact />
            <ThemeToggleButton />
          </div>
          <button
            onClick={openWhatsNew}
            data-testid="whats-new-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <Sparkle size={18} weight="fill" className="text-brand" />
            <span>{t('common.whats_new', "What's New")}</span>
          </button>
          {/* iter167 — Profile / security settings (change email + password) */}
          <button
            onClick={() => navigate('/profile')}
            data-testid="sidebar-profile-link"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <UserCircle size={18} />
            <span>{t('common.profile', 'Profile & security')}</span>
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <SignOut size={18} />
            <span>{t('common.logout', 'Sign Out')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 app-header flex items-center justify-between px-4 z-50">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-brand flex items-center justify-center">
            <Barcode weight="bold" className="text-brand-foreground" size={16} />
          </div>
          <span className="font-bold text-lg tracking-tight">STOCKARIS</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact />
          <ThemeToggleButton />
          <WarehouseSwitcher />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="p-2"
          >
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-14 pb-16 bg-background z-40 overflow-y-auto">
          <nav className="py-2 px-2">
            {renderGroupNav(true)}
          </nav>
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center font-semibold text-foreground">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <p className="font-semibold text-foreground">{user?.name}</p>
                <p className="text-sm text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full btn-mobile bg-secondary text-foreground hover:bg-accent"
            >
              <SignOut size={20} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Floating expand handle — only visible on desktop when sidebar is collapsed.
          Sits flush against the left viewport edge so it doesn't compete with content.
          Hidden in focus mode (the Exit Focus pill takes precedence). */}
      <button
        type="button"
        onClick={() => setSidebarCollapsed(false)}
        data-testid="sidebar-expand-btn"
        title="Expand sidebar (⌘B)"
        aria-label="Expand sidebar"
        className={`hidden md:flex fixed top-3 left-3 z-50 items-center justify-center w-9 h-9 bg-card border border-border rounded-md shadow-sm hover:bg-accent text-muted-foreground hover:text-foreground transition ${
          sidebarHidden && !inFocusMode ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <List size={18} weight="bold" />
      </button>

      {/* Focus-mode exit pill — appears on Picking / Packing / Receiving / Count / Driver / Carrier pages.
          Tapping it re-shows the sidebar + bottom nav for the current page only. */}
      {inFocusMode && (
        <button
          type="button"
          onClick={() => setFocusExitedRoute(location.pathname)}
          data-testid="focus-mode-exit-btn"
          title="Exit focus mode (show navigation)"
          aria-label="Exit focus mode"
          className="fixed top-3 right-3 z-50 flex items-center gap-2 bg-foreground text-background hover:bg-foreground/90 px-3 py-2 rounded-full shadow-lg text-xs font-bold uppercase tracking-wider transition"
        >
          <Target size={14} weight="fill" />
          <span className="hidden sm:inline">Focus mode</span>
          <span className="opacity-60 mx-0.5">·</span>
          <span>Exit</span>
        </button>
      )}

      {/* Main Content */}
      <main className={`flex-1 mt-14 md:mt-0 min-w-0 overflow-x-hidden transition-[margin] duration-200 ease-out ${
        sidebarHidden ? 'md:ml-0' : 'md:ml-60'
      } ${inFocusMode ? 'pb-0' : 'pb-20 md:pb-0'}`}>
        <TrialBanner />
        <UsageAlertBanner />
        <DemoBanner />
        {/* Top Bar with Warehouse Switcher */}
        <div className="hidden md:flex items-center justify-end gap-3 px-6 py-2 app-header sticky top-0 z-30">
          <WarehouseSwitcher />
        </div>
        {children}
      </main>

      {/* Mobile bottom tab bar — floor workers' primary navigation.
          Shows 5 role-aware shortcuts; the "More" tab opens the full hamburger menu.
          Hidden in focus mode so floor pages get every pixel. */}
      {!inFocusMode && (
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 h-16 app-header border-t border-border flex items-center justify-around z-40 px-1"
        data-testid="mobile-bottom-nav"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      >
        {(() => {
          const role = user?.role;
          // Role-aware shortcut set (max 4 + "More")
          const floor = ['picker', 'packer'].includes(role);
          const isPalletsOnly = productMode === 'pallets_only';
          const shortcuts = isPalletsOnly
            ? [
                { path: '/', label: 'Home', icon: House },
                { path: '/pallets', label: 'Pallets', icon: Stack },
                { path: '/pallets/scan', label: 'Scan', icon: Barcode },
                { path: '/merchants', label: 'Partners', icon: Users },
              ]
            : floor
            ? [
                { path: '/picking', label: t('nav.picking') || 'Pick', icon: HandGrabbing },
                { path: '/packing', label: t('nav.packing') || 'Pack', icon: Truck },
                { path: '/lookup', label: t('nav.lookup') || 'Lookup', icon: MagnifyingGlass },
                { path: '/leaderboard', label: 'Stats', icon: Trophy },
              ]
            : [
                { path: '/', label: 'Home', icon: House },
                { path: '/orders', label: 'Orders', icon: ShoppingCart },
                { path: '/picking', label: 'Pick', icon: HandGrabbing },
                { path: '/lookup', label: 'Lookup', icon: MagnifyingGlass },
              ];
          return (
            <>
              {shortcuts.map((s) => {
                const Ic = s.icon;
                const active = location.pathname === s.path;
                return (
                  <Link
                    key={s.path}
                    to={s.path}
                    data-testid={`mobile-tab-${s.path.replace(/\//g, '') || 'home'}`}
                    className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1 ${active ? 'text-brand' : 'text-muted-foreground'}`}
                  >
                    <Ic size={22} weight={active ? 'fill' : 'regular'} />
                    <span className="text-[10px] font-bold uppercase tracking-wider">{s.label}</span>
                  </Link>
                );
              })}
              <button
                onClick={() => setMobileMenuOpen(true)}
                data-testid="mobile-tab-more"
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1 ${mobileMenuOpen ? 'text-brand' : 'text-muted-foreground'}`}
              >
                <List size={22} weight={mobileMenuOpen ? 'fill' : 'regular'} />
                <span className="text-[10px] font-bold uppercase tracking-wider">More</span>
              </button>
            </>
          );
        })()}
      </nav>
      )}
      <GuidedTour />
      <WhatsNew />
      <ScenarioTour />

      {/* Right-click context menu for pin/unpin */}
      {contextMenu && (
        <div
          data-testid="nav-context-menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: Math.min(contextMenu.y, window.innerHeight - 120),
            left: Math.min(contextMenu.x, window.innerWidth - 220),
            zIndex: 9999,
          }}
          className="bg-popover border border-border shadow-xl min-w-[200px] py-1 rounded-md"
        >
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground border-b border-border">
            {contextMenu.label}
          </div>
          <button
            type="button"
            onClick={() => { togglePin(contextMenu.path); setContextMenu(null); }}
            data-testid="ctx-toggle-pin"
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-accent"
          >
            {pinnedPaths.includes(contextMenu.path) ? (
              <><PushPinSlash size={14} weight="bold" className="text-destructive" /> Unpin from sidebar</>
            ) : (
              <><PushPin size={14} weight="bold" className="text-brand" /> Pin to sidebar</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
